import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function About() {
  useEffect(() => {
    document.title = 'About Justine Sacco | CCO | Communications Strategist';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Learn about Justine Sacco — Chief Communications Officer at Flo Health, former CCO at Match Group, VP at FanDuel and IAC. Twenty years building narratives that hold under pressure.');

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'ProfilePage',
          '@id': `${SITE_URL}/about`,
          url: `${SITE_URL}/about`,
          name: 'About Justine Sacco | CCO | Communications Strategist',
          description: 'Chief Communications Officer at Flo Health, former CCO at Match Group, VP at FanDuel and IAC. Twenty years building narratives that hold under pressure.',
          isPartOf: { '@id': `${SITE_URL}/#website` },
          dateModified: '2026-04-02',
          inLanguage: 'en-US',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
              { '@type': 'ListItem', position: 2, name: 'About', item: `${SITE_URL}/about` },
            ],
          },
          mainEntity: {
            '@type': 'Person',
            '@id': `${SITE_URL}/#person`,
            name: 'Justine Sacco',
            jobTitle: 'Chief Communications Officer',
            description: 'Strategic communications advisor with twenty years of experience leading global communications for tech and consumer brands.',
            url: SITE_URL,
            sameAs: ['https://www.linkedin.com/in/justinesacco'],
            worksFor: { '@type': 'Organization', name: 'Flo Health', url: 'https://flo.health' },
            alumniOf: { '@type': 'CollegeOrUniversity', name: 'Tulane University' },
            hasCredential: [
              {
                '@type': 'EducationalOccupationalCredential',
                credentialCategory: 'degree',
                name: 'BA in Philosophy and Media Arts, Tulane University',
              },
            ],
            hasOccupation: [
              {
                '@type': 'Role',
                roleName: 'Chief Communications Officer',
                startDate: '2023',
                worksFor: { '@type': 'Organization', name: 'Flo Health' },
              },
              {
                '@type': 'Role',
                roleName: 'Chief Communications Officer',
                startDate: '2020',
                endDate: '2023',
                worksFor: { '@type': 'Organization', name: 'Match Group' },
              },
              {
                '@type': 'Role',
                roleName: 'VP, Corporate Communications',
                startDate: '2018',
                endDate: '2020',
                worksFor: { '@type': 'Organization', name: 'Match Group' },
              },
              {
                '@type': 'Role',
                roleName: 'VP, Corporate Communications',
                startDate: '2016',
                endDate: '2018',
                worksFor: { '@type': 'Organization', name: 'FanDuel' },
              },
              {
                '@type': 'Role',
                roleName: 'Senior Director, Corporate Communications',
                startDate: '2013',
                endDate: '2016',
                worksFor: { '@type': 'Organization', name: 'IAC' },
              },
              {
                '@type': 'Role',
                roleName: 'Corporate Communications',
                startDate: '2010',
                endDate: '2013',
                worksFor: { '@type': 'Organization', name: 'WWE' },
              },
            ],
            knowsAbout: [
              'Strategic Communications',
              'Crisis Communications',
              'Corporate Communications',
              'Reputation Management',
              'Media Relations',
              'Leadership',
            ],
          },
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-about';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('schema-about');
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      {/* Hero Statement */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h1 
            className="text-5xl md:text-7xl lg:text-8xl leading-tight mb-12"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900 }}
          >
            I DON&apos;T SPIN.<br />I BUILD NARRATIVES<br />THAT HOLD.
          </h1>
        </div>
      </section>

      {/* Bio Section */}
      <section className="py-16 px-6 bg-zinc-950">
        <div className="max-w-4xl mx-auto">
          <div 
            className="space-y-8 text-lg md:text-xl leading-relaxed text-gray-300"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            <p>
              I've spent two decades in communications. I love this field; and it’s only become more important as the world becomes more complex.
            </p>
            
            <p>
              I started in New York PR agencies — Dan Klores Communications, The Morris + King Company — where you learn fast and make lasting connections. Then WWE, which was exactly as intense as you'd imagine. High volume, high stakes, nothing off limits. You either deliver, or you leave.
            </p>
            
            <p>
              From there, IAC as Senior Director of Corporate Communications, managing comms across their portfolio of companies from Match.com, The Daily Beast, CollegeHumor, Vimeo. Different brands, different audiences, same principle: clarity under pressure.
            </p>
            
            <p>
              At FanDuel, I was VP of Corporate Communications during the most volatile regulatory period in the company's history. Daily fantasy sports were being legalized state by state, the news cycle never stopped, and every statement mattered. Crisis comms, policy comms, brand comms — all of it, all at once.
            </p>
            
            <p>
              I joined Match Group in 2018 as VP and became Chief Communications Officer in 2020. I built and led global communications for our portfolio of businesses which included Tinder, Hinge, OkCupid, and others. Scaled teams across markets, managed crises that made headlines, and ensured the narrative held even when the pressure didn't let up.
            </p>
            
            <p>
              Now I'm CCO at Flo Health, the world's leading women's health app. When your product touches reproductive health, privacy, and deeply personal decisions, trust isn't a brand value. It's the entire foundation. Every word matters. Every decision is scrutinized. That's the work.
            </p>
            
            <p>
              I studied Philosophy and Media Arts at Tulane University. People ask why philosophy. Because it taught me to think clearly, argue precisely, and mean what I say. Those skills matter more in communications than any PR textbook ever will.
            </p>
          </div>
        </div>
      </section>

      {/* Credentials Bar */}
      <section className="py-16 px-6 border-t border-zinc-800">
        <div className="max-w-5xl mx-auto">
          <h2 
            className="text-3xl mb-8 text-[#FF4D00]"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900 }}
          >
            EXPERIENCE
          </h2>
          <div 
            className="space-y-6 text-gray-400"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            <div className="flex flex-col md:flex-row md:items-baseline md:justify-between border-l-2 border-[#FF4D00] pl-6">
              <strong className="text-white text-lg">Chief Communications Officer</strong>
              <span className="text-sm">Flo Health — Present</span>
            </div>
            <div className="flex flex-col md:flex-row md:items-baseline md:justify-between border-l-2 border-zinc-700 pl-6">
              <strong className="text-white text-lg">Chief Communications Officer</strong>
              <span className="text-sm">Match Group — 2020–2023</span>
            </div>
            <div className="flex flex-col md:flex-row md:items-baseline md:justify-between border-l-2 border-zinc-700 pl-6">
              <strong className="text-white text-lg">VP, Corporate Communications</strong>
              <span className="text-sm">Match Group — 2018–2020</span>
            </div>
            <div className="flex flex-col md:flex-row md:items-baseline md:justify-between border-l-2 border-zinc-700 pl-6">
              <strong className="text-white text-lg">VP, Corporate Communications</strong>
              <span className="text-sm">FanDuel — 2016–2018</span>
            </div>
            <div className="flex flex-col md:flex-row md:items-baseline md:justify-between border-l-2 border-zinc-700 pl-6">
              <strong className="text-white text-lg">Senior Director, Corporate Communications</strong>
              <span className="text-sm">IAC — 2013–2016</span>
            </div>
            <div className="flex flex-col md:flex-row md:items-baseline md:justify-between border-l-2 border-zinc-700 pl-6">
              <strong className="text-white text-lg">Corporate Communications</strong>
              <span className="text-sm">WWE — 2010–2013</span>
            </div>
            <div className="flex flex-col md:flex-row md:items-baseline md:justify-between border-l-2 border-zinc-700 pl-6">
              <strong className="text-white text-lg">PR Agencies</strong>
              <span className="text-sm">Dan Klores Communications, The Morris + King Company</span>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-zinc-800">
            <h3 
              className="text-xl mb-2 text-white"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900 }}
            >
              EDUCATION
            </h3>
            <p 
              className="text-gray-400"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              BA in Philosophy and Media Arts — Tulane University
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
